<template>
  <div class="h-full flex flex-col">
    <p class="flex items-center text-sm text-gray-700 leading-snug py-10 px-20 bg-gray-100 border-b border-gray-300
    mobile-l:px-25 tablet:px-35">
      <FontAwesomeIcon
        :icon="['fab', 'apple']"
        class="flex-shrink-0 text-lg text-gray-600 mr-6"
      />
      <FontAwesomeIcon
        :icon="['fab', 'android']"
        class="flex-shrink-0 text-lg text-gray-600 mr-12"
      />
      <span>Team roles also apply in the Eventor iOS and Android apps.</span>
    </p>
    <div
      v-loading="!teamMembersInitLoaded"
      element-loading-text="Getting your team members.."
      class="view-base flex-1 pb-80 laptop:pb-25"
    >
      <!-- Teams this user was added to by other event owners (from the events shared with them) -->
      <section
        v-if="memberships.length"
        class="mb-40"
      >
        <h2 class="mb-15">
          <span class="font-normal text-lg">Teams you're part of</span>
        </h2>
        <ul class="team-members-grid grid grid-gap-20">
          <TeamMembershipCard
            v-for="membership in memberships"
            :key="membership.ownerId"
            :membership="membership"
          />
        </ul>
      </section>

      <div
        v-show="teamMembersInitLoaded"
        :class="{
          'h-full flex flex-col items-center justify-center pb-40': !allSortedTeamMembers.length && !memberships.length,
          'flex flex-col items-start': !allSortedTeamMembers.length && memberships.length,
        }"
      >
        <template v-if="!allSortedTeamMembers.length && memberships.length">
          <h2 class="font-normal text-lg">Your team</h2>
          <p class="text-sm text-primary-light leading-snug mt-4">
            You have not added anyone to your own events yet.
          </p>
        </template>
        <template v-else-if="!allSortedTeamMembers.length">
          <FontAwesomeIcon
            :icon="['fad', 'users']"
            class="h-80 flex items-center text-6xl text-gray-700 p-15 mt-20 bg-gray-200 rounded-full"
          />
          <h2 class="text-xl text-center leading-tight mt-30">
            Add your first team member
          </h2>
        </template>
        <h2 v-else>
          <span class="text-2xl mr-8">{{ allSortedTeamMembers.length }}</span>
          <span class="font-normal text-lg">{{ mainTitle }}</span>
        </h2>
        <button
          class="btn-primary"
          :class="{
            'btn-type-round flex items-center py-12 mt-20': !allSortedTeamMembers.length,
            'self-start': !allSortedTeamMembers.length && memberships.length,
            'btn-type-fab mt-2 laptop:btn-type-round': allSortedTeamMembers.length,
          }"
          @click="onActionButtonPress('add')"
        >
          <FontAwesomeIcon
            :icon="['far', 'plus']"
            class="text-2xl btn-icon"
          />
          <span class="btn-label">
            Add member
          </span>
        </button>
      </div>
      <TeamMemberList
        v-if="allSortedTeamMembers.length"
        :team-members="allSortedTeamMembers"
        class="mt-15 mb-15 mobile-l:mb-0 laptop:mt-40"
      >
        <TeamMemberCard
          v-for="(teamMember, index) in allSortedTeamMembers"
          :key="`team-member-${index}`"
          :team-member="teamMember"
          @update="onActionButtonPress('update', $event)"
          @remove="onActionButtonPress('remove', $event)"
        />
      </TeamMemberList>
      <UIModal
        v-model="modal.isShowing"
        :title="modalTitle"
        :content-description="modalTitle"
        :close-on-backdrop-press="!modal.loading.status"
        :close-on-escape-press="!modal.loading.status"
        @hidden="onModalHidden"
        @shown="onModalShown"
      >
        <template #header="{ hideModal }">
          <button
            type="button"
            title="Close"
            aria-label="Close"
            :disabled="modal.loading.status"
            class="modal-close-button absolute top-0 right-0 h-40 w-40 flex items-center justify-center mt-12 mr-12
            rounded-full focus:outline-none z-20"
            :class="modal.loading.status ? 'opacity-75 cursor-not-allowed' : 'hover:bg-gray-200 focus:bg-gray-200'"
            @click="hideModal"
          >
            <FontAwesomeIcon
              :icon="['fal', 'times']"
              class="text-2xl text-gray-600"
            />
          </button>
          <h2
            v-if="modal.type !== 'remove'"
            class="font-medium text-xl text-center text-primary-dark leading-tight pt-25 px-35 mb-10"
          >
            {{ modalTitle }}
          </h2>
          <div
            v-else
            class="flex flex-col items-center"
          >
            <div class="relative mt-20">
              <FontAwesomeIcon
                :icon="['fal', 'user-circle']"
                class="text-6xl text-gray-400"
              />
              <FontAwesomeIcon
                :icon="['fad', 'exclamation-triangle']"
                class="icon-remove absolute bottom-0 right-0 text-2xl"
              />
            </div>
            <h2 class="font-medium text-xl text-primary-dark leading-tight mt-20 px-35">
              {{ modalTitle }}
            </h2>
          </div>
        </template>
        <template #default="{ hideModal }">
          <TeamMemberForm
            v-if="modal.type === 'add' || modal.type === 'update'"
            ref="team-member-form"
            :existing-team-member="formTeamMember"
            :no-password-update="modal.type === 'update'"
            class="px-35"
            @submit="onSuccessfulTeamMemberFormSubmit"
          >
            <template #default="{ submitForm }">
              <div class="btn-2-modal py-20 px-35 -mx-35 bg-gray-100 mobile-l:py-15">
                <button
                  class="w-full font-medium text-sm text-white tracking-wide py-12 px-30 bg-secondary rounded-8
                  shadow-1dp focus:outline-none hover:opacity-90 mobile-l:w-auto tablet:w-auto tablet:px-40"
                  @click="submitForm"
                >
                  {{ modal.type === 'add' ? 'Add member' : 'Save member' }}
                </button>
                <button
                  class="w-full font-medium text-sm text-gray-600 tracking-wide text-center py-10 px-30
                  rounded-8 focus:outline-none hover:bg-gray-300 mobile-l:w-auto tablet:w-auto tablet:px-30 tablet:mb-0"
                  @click="hideModal"
                >
                  Cancel
                </button>
              </div>
            </template>
          </TeamMemberForm>
          <div
            v-else
            class="text-primary flex items-center px-35 mt-10 mb-30"
          >
            <p>
              Are you sure you want to remove
              <span class="font-bold">
                {{ modal.teamMember.name }}
              </span>
              from your team?
            </p>
          </div>
        </template>
        <template
          v-if="modal.type === 'remove'"
          #footer="{ hideModal }"
        >
          <div class="btn-2-modal py-20 px-35 bg-gray-100 mobile-l:py-15">
            <button
              type="submit"
              class="btn-base btn-danger w-full font-medium text-sm tracking-wide rounded-6 shadow-1dp
              focus:outline-none hover:opacity-90 mobile-l:w-auto"
              @click="removeTeamMember"
            >
              Remove member
            </button>
            <button
              class="btn-base w-full font-medium text-sm text-gray-600 tracking-wide text-center rounded-6
              focus:outline-none hover:bg-gray-300 mobile-l:w-auto tablet:mb-0"
              @click="hideModal"
            >
              Cancel
            </button>
          </div>
        </template>
        <template #after>
          <Transition
            name="fade-slowest"
            mode="out-in"
            appear
          >
            <div
              v-show="modal.loading.status"
              class="absolute h-full w-full flex flex-col items-center justify-center bg-gray-200 rounded-8 z-10"
            >
              <UILoader
                v-show="modal.loading.status"
                class="text-5xl text-secondary mb-10"
              />
              <span
                v-if="modal.loading.text"
                class="text-2xl text-center text-secondary leading-tight px-40"
              >
                {{ modal.loading.text }}
              </span>
            </div>
          </Transition>
        </template>
      </UIModal>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { FSGetUserTeamMembers, FSGetUserEvents, FSGetEventMember } from '@/helpers/firestoreRefs';
import { showSuccessNotification, showErrorNotification } from '@/utils/notification';
import {
  APIAddTeamMember, APIUpdateTeamMember, APIRemoveTeamMember, APIRefreshTeamMembersStatus,
} from '@/api';
import UILoader from '@/components/UI/UILoader';
import UIModal from '@/components/UI/UIModal';
import TeamMemberList from '@/components/teamMember/TeamMemberList';
import TeamMemberCard from '@/components/teamMember/TeamMemberCard';
import TeamMemberForm from '@/components/teamMember/TeamMemberForm';
import TeamMembershipCard from '@/components/teamMember/TeamMembershipCard';
import formatMonthAndDay from '@/utils/format/date-time/monthAndDay';

const memberRoleLabels = {
  guestsCheckIn: 'Guest check-in/out only',
  guestsFullAccess: 'Manager',
  eventPromoter: 'Promoter',
};

const defaultTeamMemberFields = {
  name: '',
  email: '',
  password: '',
  role: 'guestsCheckIn',
  maxGuests: 0,
};

export default {
  components: {
    UILoader,
    UIModal,
    TeamMemberList,
    TeamMemberCard,
    TeamMemberForm,
    TeamMembershipCard,
  },
  data: () => ({
    memberships: [],
    unsubscribeUserTeamMembers: null,
    replaceExistingTeamMembers: true,
    modal: {
      isShowing: false,
      type: 'add',
      loading: {
        status: false,
        text: '',
      },
      teamMember: {
        id: '',
        ...defaultTeamMemberFields,
      },
    },
  }),
  created() {
    // TODO: Check handling when user is logged-out (from another tab for example)
    this.unsubscribeUserTeamMembers = this.subscribeUserTeamMembers(this.userAuthDataId);
    // Updates "Invitation accepted/pending" from the members' sign-ins; results arrive through the snapshot
    APIRefreshTeamMembersStatus().catch((error) => console.error(error));
    this.loadMemberships();
  },
  computed: {
    ...mapGetters('user/teamMembers', {
      teamMembersInitLoaded: 'teamMembersInitLoaded',
      allSortedTeamMembers: 'allSortedTeamMembers',
    }),
    ...mapGetters('user/auth', {
      userAuthDataId: 'authDataId',
    }),
    mainTitle() {
      return `team member${this.allSortedTeamMembers.length !== 1 ? 's' : ''}`;
    },
    modalTitle() {
      if (this.modal.type === 'add') return 'Add new member';
      if (this.modal.type === 'update') return `Update ${this.modal.teamMember.name}`;
      return `Remove ${this.modal.teamMember.name}`;
    },
    formTeamMember() {
      return this.modal.teamMember.id ? this.modal.teamMember : null;
    },
  },
  methods: {
    ...mapActions('user/teamMembers', {
      onTeamMembersSnapshot: 'onSnapshot',
    }),
    // Events shared with this user by other owners, grouped per owner, with the user's role in that owner's team
    async loadMemberships() {
      const uid = this.userAuthDataId;
      if (!uid) return;
      try {
        const snapshot = await FSGetUserEvents(uid).get();
        const byOwner = {};
        snapshot.docs.forEach((doc) => {
          const event = doc.data();
          if (!event.ownerId || event.ownerId === uid) return;
          if (!byOwner[event.ownerId]) {
            byOwner[event.ownerId] = {
              ownerId: event.ownerId,
              ownerName: (event.owner && event.owner.name) || '',
              ownerEmail: (event.owner && event.owner.email) || '',
              role: null,
              events: [],
            };
          }
          const owner = byOwner[event.ownerId];
          if (!owner.ownerEmail && event.owner && event.owner.email) owner.ownerEmail = event.owner.email;
          const start = event.startingTime && typeof event.startingTime.toDate === 'function'
            ? event.startingTime.toDate() : null;
          owner.events.push({
            id: doc.id,
            title: event.title || 'Untitled event',
            status: event.status,
            start,
            dateLabel: start ? formatMonthAndDay(start) : '',
          });
        });

        const memberships = await Promise.all(Object.values(byOwner).map(async (owner) => {
          // Active events first (soonest first), archived at the end (latest first)
          const events = [...owner.events].sort((a, b) => {
            if ((a.status === 1) !== (b.status === 1)) return a.status === 1 ? 1 : -1;
            const direction = a.status === 1 ? -1 : 1;
            return direction * ((a.start ? a.start.getTime() : 0) - (b.start ? b.start.getTime() : 0));
          });
          // The role is the same on all of the owner's events; read it from one of them
          let role = null;
          try {
            const member = await FSGetEventMember(events[0].id, uid).get();
            role = member.exists ? member.data().role : null;
          } catch (error) {
            console.error(error);
          }
          // Same fallback as the Android app: a team member without a role document can check guests in
          return {
            ...owner, events, role, roleLabel: memberRoleLabels[role] || memberRoleLabels.guestsCheckIn,
          };
        }));

        this.memberships = memberships.sort((a, b) => a.ownerName.localeCompare(b.ownerName));
      } catch (error) {
        console.error(error);
      }
    },
    onModalHidden() {
      this.modal.loading.status = false;
      this.modal.loading.text = '';

      if (!this.$refs['team-member-form']) return;
      this.$refs['team-member-form'].resetForm();
    },
    onModalShown() {
      if (this.modal.type === 'remove') return;
      this.$refs['team-member-form'].focusFirstInputField();
    },
    subscribeUserTeamMembers(userId) {
      return FSGetUserTeamMembers(userId)
        .orderBy('name')
        .onSnapshot((snapshot) => {
          this.onTeamMembersSnapshot({
            snapshot,
            replaceExistingState: this.replaceExistingTeamMembers,
          });

          // Don't replace the team members on subsequent snapshots
          this.replaceExistingTeamMembers = false;
        }, (error) => {
          console.error(error);
        });
    },
    async onSuccessfulTeamMemberFormSubmit(teamMember) {
      // Add/Update of team member
      try {
        if (this.modal.type === 'add') {
          this.modal.loading.text = `Adding ${teamMember.name}..`;
          this.modal.loading.status = true;

          await this.addTeamMember(teamMember);
          showSuccessNotification(`${teamMember.name} was added successfully.`);
        } else if (this.modal.type === 'update') {
          this.modal.loading.text = `Updating ${teamMember.name}..`;
          this.modal.loading.status = true;

          await this.updateTeamMember(this.modal.teamMember.id, teamMember);
          showSuccessNotification(`${teamMember.name} was updated successfully.`);
        }

        this.modal.isShowing = false;
      } catch (error) {
        console.error(error);
        this.modal.loading.status = false;
        showErrorNotification(error.message);
      }
    },
    onActionButtonPress(actionType, teamMember) {
      if (actionType === 'add') {
        this.modal.teamMember = {
          id: '',
          ...defaultTeamMemberFields,
        };
      } else { // Update/Remove
        const {
          id, name, email, password, role, maxGuests,
        } = teamMember;

        this.modal.teamMember = {
          id,
          ...(name && { name }),
          ...(email && { email }),
          ...(password && { password }),
          ...(role && { role }),
          ...(maxGuests && { maxGuests }),
        };
      }

      this.modal.type = actionType;
      this.modal.isShowing = true;
    },
    addTeamMember(teamMember) {
      return APIAddTeamMember(teamMember)
        .then(() => Promise.resolve())
        .catch((error) => Promise.reject(error));
    },
    updateTeamMember(id, teamMember) {
      return APIUpdateTeamMember({
        teamMemberId: id,
        ...teamMember,
      })
        .then(() => Promise.resolve())
        .catch((error) => Promise.reject(error));
    },
    removeTeamMember() {
      this.modal.loading.status = true;
      this.modal.loading.text = `Removing ${this.modal.teamMember.name}`;

      APIRemoveTeamMember({ teamMemberId: this.modal.teamMember.id })
        .then(() => {
          this.modal.isShowing = false;
          showSuccessNotification(`${this.modal.teamMember.name} was deleted successfully.`);
        })
        .catch((error) => {
          console.error(error);
          this.modal.loading.status = false;
          showErrorNotification(error.message, 4000);
        });
    },
  },
  beforeDestroy() {
    if (this.unsubscribeUserTeamMembers !== null) this.unsubscribeUserTeamMembers();
  },
};
</script>

<style scoped>
/* Same grid as TeamMemberList: one column on phones, 320px+ columns from tablet */
.team-members-grid {
  grid-template-columns: minmax(0, 1fr);
}

@screen tablet {
  .team-members-grid {
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  }
}

/* Button sizes */
.btn-base {
  @apply py-10 px-30;
}

/* Button colors (text + background) */
.btn-primary {
  @apply text-white bg-secondary;
}
.btn-danger {
  @apply text-white bg-danger;
}

/* Button positions */
/* btn-2-modal: 2 button modal position with cancel button first semantically (For desktop)  */
.btn-2-modal {
  @apply flex flex-col items-center;

  :nth-child(2) {
    @apply mt-5;
  }
}

@screen mobile-l {
  .btn-2-modal {
    @apply flex-row;

    :nth-child(2) {
      @apply mt-0 ml-5;
    }
  }

  @screen touch-device {
    .btn-2-modal {
      @apply flex-row-reverse;

      :nth-child(2) {
        @apply ml-0 mr-5;
      }
    }
  }
}

.icon-remove {
  --fa-primary-color: theme('colors.white');
  --fa-primary-opacity: 1;
  --fa-secondary-color: theme('colors.danger');
  --fa-secondary-opacity: 1;
}
</style>
